// En qué orden van las cards por destino del carrusel de la home del teléfono, según lo que la
// persona dijo en el onboarding que quería crear
// (ADR-2026-10-12-el-carrusel-arranca-por-lo-que-elegiste-en-el-onboarding).
//
// La respuesta vive en `company_profiles.tipos_contenido`: los ids de la pregunta "¿Qué tipo
// de contenido estás buscando crear?" (cuestionario) o "¿Qué te gustaría crear primero?"
// (onboarding de marca), en el orden en que se tocaron — el primero es el principal.
//
// Puro: es lo que se testea.

import type { Seccion } from '../../estilos/CabeceraPorDestino';

/**
 * Destinos que no se muestran en NINGÚN menú (la home de escritorio, el carrusel y el sheet
 * del teléfono): "Anuncios" se oculta hasta que tenga modos, en vez de quedar como
 * "Próximamente" (ADR-2026-10-26-anuncios-se-oculta-hasta-tener-modos). Para volver a
 * mostrarlo, sacarlo de acá: su card sigue definida en `SECCIONES`.
 */
export const DESTINOS_OCULTOS: ReadonlySet<Seccion> = new Set<Seccion>(['anuncios']);

/** Los destinos que se dibujan, en el orden de siempre. */
export const DESTINOS_VISIBLES: readonly Seccion[] = (['redes', 'catalogos', 'anuncios'] as const).filter(
  (s) => !DESTINOS_OCULTOS.has(s),
);

/** El orden de siempre, para quien no respondió o eligió algo que no es un destino. */
export const ORDEN_POR_DEFECTO: readonly Seccion[] = DESTINOS_VISIBLES;

/**
 * A qué destino lleva cada respuesta. "Videos" va a Redes sociales: es donde la home ofrece
 * crear videos (posteos, carruseles y videos). "Otro" no lleva a ninguno.
 */
const DESTINO_DE_RESPUESTA: Readonly<Record<string, Seccion>> = {
  fotos_redes: 'redes',
  videos: 'redes',
  foto_producto: 'catalogos',
  anuncios: 'anuncios',
};

/**
 * Destinos que todavía no se pueden usar: no van primeros aunque se hayan elegido (la card
 * dice "Próximamente" y no abre nada). Se toma la siguiente respuesta.
 */
const NO_DISPONIBLES: ReadonlySet<Seccion> = new Set<Seccion>(['anuncios']);

/** El destino que va primero: la primera respuesta que lleva a uno disponible. */
export function destinoPrincipal(tiposDeContenido: readonly string[] | null | undefined): Seccion | null {
  for (const id of tiposDeContenido ?? []) {
    const destino = DESTINO_DE_RESPUESTA[id];
    if (destino && !NO_DISPONIBLES.has(destino)) return destino;
  }
  return null;
}

/** El orden del carrusel: el principal primero y el resto en el orden de siempre. */
export function ordenDeDestinos(tiposDeContenido: readonly string[] | null | undefined): Seccion[] {
  const principal = destinoPrincipal(tiposDeContenido);
  if (!principal) return [...ORDEN_POR_DEFECTO];
  return [principal, ...ORDEN_POR_DEFECTO.filter((s) => s !== principal)];
}
